import React, { useCallback, useEffect, useState } from 'react';
import { View, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { dispatchService } from '../../../services/central-api/dispatch';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import '../../../styles/date-ranger-picker.css';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import ReportModal from './ReportModal';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function Reports({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DispatchParamList, 'DispatchReports'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['DispatchReports']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [reports, setReports] = useState<ReportOccurrence[]>([]);
    const [totalItems, setTotalItems] = useState(0);

    function handleFormClose() {
        refetch();
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, navigation, filters]));

    function canDownloadReport(row: ReportOccurrence) {
        return (
            authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.dispatch_module_access
            || authenticatedUser?.permissions.dispatch_download_report
            || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_download_report
        ) && row.status === 'success' && !isDownloading;
    }

    const queryResult = useQuery({
        queryKey: ['occurrenceReports', filters],
        queryFn: () => dispatchService.getReports(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setReports(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('reports')}
                actions={<> {
                    (
                        authenticatedUser?.isAdmin
                        || authenticatedUser?.permissions.dispatch_module_access
                        || authenticatedUser?.permissions.dispatch_generate_report
                        || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports
                    ) &&
                    <ReportModal onClose={handleFormClose} />
                }</>}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredReports')}
                    zIndex={2}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true
                            },
                            {
                                name: translate('user'),
                                selector: row => row.user?.name || '',
                                wrap: true
                            },
                            {
                                name: 'Token',
                                selector: row => row.token,
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center' }}>
                                        {row.status == 'pending' ?
                                            <ActivityIndicator size={16} color={theme.colors.loadingColor} /> :
                                            <FontAwesomeIcon style={styles.statusStyle(row.status)} fontSize={16} icon={row.status == 'success' ? faCheckCircle : faTimesCircle} />}
                                    </View>,
                                wrap: true,
                                grow: 0
                            },
                            {
                                name: translate('filters'),
                                cell: row => <View style={{ width: '100%' }}>
                                    <View style={{ flexDirection: 'column', rowGap: 5 }}>
                                        <MyAppText>{translate('format')}: {row.filters.isPdf ? 'PDF' : row.filters.isCsv ? 'CSV' : 'XLSX'}</MyAppText>
                                        {row.filters.startDate && <MyAppText>{translate('start')}: {moment(row.filters.startDate).format('DD/MM/YYYY')}</MyAppText>}
                                        {row.filters.endDate && <MyAppText>{translate('end')}: {moment(row.filters.endDate).format('DD/MM/YYYY')}</MyAppText>}
                                        {
                                            (authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports)
                                            && row.actingBody && <MyAppText>{translate('actingBody')}: {row.actingBody.name}</MyAppText>
                                        }
                                        {row.filters.assumedBy && <MyAppText>{translate('assumedBy')}: {row.filters.assumedBy}</MyAppText>}
                                        {row.filters.receivedBy && <MyAppText>{translate('receivedBy')}: {row.filters.receivedBy}</MyAppText>}
                                        {row.filters.transferredBy && <MyAppText>{translate('transferredBy')}: {row.filters.transferredBy}</MyAppText>}
                                        {row.filters.finishedBy && <MyAppText>{translate('finishedBy')}: {row.filters.finishedBy}</MyAppText>}
                                        {row.filters.actingBodyCommands && row.filters.actingBodyCommands.length > 0 &&
                                            <MyAppText>{translate('commands')}: {row.filters.actingBodyCommands.join(',')}</MyAppText>
                                        }
                                        {row.filters.triggerTypes && row.filters.triggerTypes.length > 0 &&
                                            <MyAppText>{translate('integrationType')}: {row.filters.triggerTypes.map(tt => translate(tt)).join(',')}</MyAppText>
                                        }
                                        {row.filters.occurrenceTypes && row.filters.occurrenceTypes.length > 0 &&
                                            <MyAppText>{translate('occurrenceType')}: {row.filters.occurrenceTypes.join(',')}</MyAppText>
                                        }
                                    </View>
                                </View>
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                                wrap: true
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBody ? row.actingBody.name : '',
                            },
                            {
                                name: translate('tags'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5, paddingVertical: 5 }}>
                                        {row.tags ?
                                            row.tags.map((tag) =>
                                                <View key={tag.id} style={[styles.tagsMarker, { backgroundColor: tag.color }]}>
                                                    <MyAppText style={styles.markerText(tag.color)}>
                                                        {tag.name}
                                                    </MyAppText>
                                                </View>
                                            ) : null
                                        }
                                    </View>
                            },
                            {
                                button: true,
                                cell: row =>
                                    canDownloadReport(row) ? <DropdownButton
                                        actions={[
                                            {
                                                content: translate('downloadReport'),
                                                onPress: async () => {
                                                    if (!canDownloadReport(row)) {
                                                        return;
                                                    }

                                                    try {
                                                        setIsDownloading(true);
                                                        const video = await dispatchService.getReport(row.id);
                                                        window.open(video.resultUrl);
                                                    } catch (error) {
                                                        Toast.show({
                                                            type: 'sentinelxError',
                                                            text1: translate('unexpectedError'),
                                                        });
                                                    } finally {
                                                        setIsDownloading(false);
                                                    }
                                                }
                                            }
                                        ]}
                                    /> : null
                            }
                        ]}
                        data={reports}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
    tagsMarker: {
        borderRadius: 8,
        minHeight: 16,
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: '100%'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
}));
